import isArray from "lodash.isarray";
import isEqual from "lodash.isequal";
import isObject from "lodash.isobject";
import transform from "lodash.transform";

import {
  ExperienceDto,
  ExperienceListDto,
  ExperienceListVariationDto,
  ExperienceTestType,
  ExperienceType,
  ModKeys,
  OfferDto,
  PROPENSITY_MOD_POLICY,
  ScheduleDto,
} from "@intelligems/ig-types";

export const MISSING_STOREFRONT_TOKEN_ERROR =
  "Please refresh permissions to use this modification.";
import {
  getEndedAtTs,
  getPausedAtTs,
  getStartedAtTs,
} from "@intelligems/ig-analytics";
import { capitalizeFirstLetter } from "@intelligems/ig-utils";
import { FoundExperimentDetails } from "@src/types/experience";
import { shortDateTimeConverter } from "@src/utils/dateConverters";

export const DISCOUNT_TIER_LIMIT = 7;

export const getPersonalizationDuration = (
  experience: ExperienceListDto,
  storeCurrencyLocale: string,
  schedules?: ScheduleDto[]
) => {
  const pausedAtTs = getPausedAtTs(experience.experienceActions)?.actionTs;
  const endedAtTs = getEndedAtTs(experience.experienceActions)?.actionTs;
  const latestStartedAt = getStartedAtTs(
    experience.experienceActions
  )?.actionTs;

  if (schedules && schedules.length > 0) {
    const experienceSchedules = schedules.filter(
      (schedule) => schedule.experienceId === experience.id
    );

    if (experienceSchedules.length > 0) {
      const startSchedule = experienceSchedules.find(
        (schedule) => schedule.action === "start"
      );
      const pauseSchedule = experienceSchedules.find(
        (schedule) => schedule.action === "pause"
      );

      if (startSchedule) {
        const startDate = shortDateTimeConverter(
          startSchedule.executionTime,
          storeCurrencyLocale,
          undefined,
          undefined,
          true
        );

        if (pauseSchedule) {
          const pauseDate = shortDateTimeConverter(
            pauseSchedule.executionTime,
            storeCurrencyLocale,
            undefined,
            undefined,
            true
          );
          return `Scheduled: ${startDate} - ${pauseDate}`;
        } else {
          return `Scheduled: ${startDate} onwards`;
        }
      }
    }
  }

  if (experience.status === "pending") {
    return `Created ${shortDateTimeConverter(
      experience.createdAtTs,
      storeCurrencyLocale,
      undefined,
      undefined,
      true
    )}`;
  }
  if (experience.status === "started") {
    return `Active since ${shortDateTimeConverter(
      latestStartedAt,
      storeCurrencyLocale,
      undefined,
      undefined,
      true
    )}`;
  }
  return `${shortDateTimeConverter(
    latestStartedAt,
    storeCurrencyLocale,
    undefined,
    undefined,
    true
  )} - ${shortDateTimeConverter(
    pausedAtTs ?? endedAtTs,
    storeCurrencyLocale,
    undefined,
    undefined,
    true
  )}`;
};

export const isUsedInExperiment = (
  experienceList: ExperienceListDto[],
  campaignId: string | null
): FoundExperimentDetails => {
  let experimentFound: FoundExperimentDetails = {
    name: undefined,
    id: undefined,
  };

  for (const experiment of experienceList) {
    for (const group of experiment.variations) {
      if (
        group.testExperienceId === campaignId &&
        experiment.status === "started"
      ) {
        experimentFound = { name: experiment.name, id: experiment.id };
        break;
      }
    }
  }

  return experimentFound;
};

export const isUsedInPersonalization = (
  personalizationsList: ExperienceDto[],
  experienceVariations: ExperienceListVariationDto[]
): FoundExperimentDetails[] => {
  const matchingPersonalizations: FoundExperimentDetails[] = [];

  for (const personalization of personalizationsList) {
    for (const variation of experienceVariations) {
      if (
        variation.testExperienceId === personalization.id &&
        personalization.status === "started"
      ) {
        matchingPersonalizations.push({
          name: personalization.name,
          id: personalization.id,
        });
        break;
      }
    }
  }

  return matchingPersonalizations;
};

export const difference = (origObj: any, newObj: any): any => {
  function changes(newObj: any, origObj: any) {
    let arrayIndexCounter = 0;
    return transform(newObj, function (result, value, key) {
      if (!isEqual(value, origObj[key])) {
        const resultKey = isArray(origObj) ? arrayIndexCounter++ : key;
        // @ts-ignore
        result[resultKey] =
          isObject(value) && isObject(origObj[key])
            ? changes(value, origObj[key])
            : value;
      }
    });
  }

  return changes(newObj, origObj);
};

export const isFreeShippingDisabled = (
  discountState: OfferDto,
  currTierIndex: number
) => {
  const { tiers } = discountState;
  const tier = tiers[currTierIndex];
  if (tier.isFreeShipping) {
    for (let i = 0; i < currTierIndex; i++) {
      if (tiers[i].isFreeShipping) {
        return true;
      }
    }
  } else {
    for (let i = currTierIndex + 1; i < tiers.length; i++) {
      if (!tiers[i].isFreeShipping) {
        return true;
      }
    }
  }

  return false;
};

const priceIncompatabilityLabels: Record<string, string> = {
  tieredDiscount: "an offer",
  cartDiscount: "an offer",
  freeGift: "an offer",
  freeShipping: "an offer",
  buyXGetY: "an offer",
  checkoutBlock: "a checkout experience",
  postPurchase: "a post-purchase experience",
  offer: "an offer",
  price: "a price",
  onsiteUpsell: "an onsite upsell",
};

const modificationDisabledReasons = {
  freeGift: [
    "tieredDiscount",
    "cartDiscount",
    "freeShipping",
    "buyXGetY",
    "price",
    "offer",
    "checkoutBlock",
    "postPurchase",
    "onsiteUpsell",
  ],
  tieredDiscount: [
    "freeGift",
    "cartDiscount",
    "freeShipping",
    "buyXGetY",
    "price",
    "offer",
    "checkoutBlock",
    "postPurchase",
    "onsiteUpsell",
  ],
  cartDiscount: [
    "freeGift",
    "tieredDiscount",
    "freeShipping",
    "buyXGetY",
    "price",
    "offer",
    "checkoutBlock",
    "postPurchase",
    "onsiteUpsell",
  ],
  freeShipping: [
    "freeGift",
    "tieredDiscount",
    "cartDiscount",
    "buyXGetY",
    "price",
    "offer",
    "checkoutBlock",
    "postPurchase",
  ],
  buyXGetY: [
    "freeGift",
    "tieredDiscount",
    "cartDiscount",
    "freeShipping",
    "price",
    "offer",
    "checkoutBlock",
    "postPurchase",
    "onsiteUpsell",
  ],
  template: ["theme"],
  theme: ["template"],
  price: [
    "tieredDiscount",
    "cartDiscount",
    "freeGift",
    "freeShipping",
    "buyXGetY",
    "offer",
    "checkoutBlock",
    "onsiteUpsell",
  ],
  offer: [
    "price",
    "tieredDiscount",
    "cartDiscount",
    "freeGift",
    "freeShipping",
    "buyXGetY",
    "checkoutBlock",
    "postPurchase",
    "onsiteUpsell",
  ],
  checkoutBlock: [
    "price",
    "tieredDiscount",
    "cartDiscount",
    "freeGift",
    "freeShipping",
    "buyXGetY",
    "offer",
    "onsiteUpsell",
  ],
  onsiteUpsell: [
    "price",
    "tieredDiscount",
    "cartDiscount",
    "freeGift",
    "freeShipping",
    "offer",
    "checkoutBlock",
  ],
  postPurchase: [
    "tieredDiscount",
    "cartDiscount",
    "freeGift",
    "freeShipping",
    "buyXGetY",
    "offer",
  ],
};

export const areOtherOffersDisabled = (
  key: ModKeys,
  modificationsInUse: string[],
  tempModsToAdd: string[],
  isHeadless: boolean,
  hasFeatureAccess: boolean,
  isPersonalizationActive: boolean,
  shouldDuplicateProducts: boolean,
  showInactiveSubWarning,
  isViewMode: boolean,
  isPersonalization: boolean,
  isUnderOfferLimit: boolean,
  experienceType?: ExperienceType,
  hasStorefrontToken?: boolean,
  hasPurchaseLikelihoodTargeting?: boolean
) => {
  if (isViewMode) {
    return {
      isDisabled: true,
      reason:
        "Please unarchive this personalization to add more modifications.",
    };
  }
  if (showInactiveSubWarning) {
    return {
      isDisabled: true,
      reason: "Approve billing to add modifications to this personalization.",
    };
  }
  if (isPersonalizationActive && !hasFeatureAccess) {
    return {
      isDisabled: true,
      reason: "Ineligible mod and active personalization",
    };
  }

  const propensityBlockLabel = PROPENSITY_MOD_POLICY[key];
  if (hasPurchaseLikelihoodTargeting && propensityBlockLabel) {
    return {
      isDisabled: true,
      reason: `A ${propensityBlockLabel} modification cannot be used with purchase likelihood targeting.`,
    };
  }

  if (key === "price" && shouldDuplicateProducts && isPersonalization) {
    return {
      isDisabled: true,
      reason: "Duplicate products are not supported for personalizations.",
    };
  }

  if (key === "price" && experienceType === "personalization/offer") {
    return {
      isDisabled: true,
      reason: `A price modification cannot be used in an offer ${isPersonalization ? "personalization" : "test"}.`,
    };
  }
  if (key === "price" && experienceType === "checkoutBlock") {
    return {
      isDisabled: true,
      reason: `A price modification cannot be used in a checkout ${isPersonalization ? "personalization" : "test"}.`,
    };
  }

  if (
    (key === "tieredDiscount" ||
      key === "cartDiscount" ||
      key === "freeGift" ||
      key === "freeShipping" ||
      key === "buyXGetY" ||
      key === "offer") &&
    experienceType === "checkoutBlock"
  ) {
    return {
      isDisabled: true,
      reason: `An offer modification cannot be used in a checkout ${isPersonalization ? "personalization" : "test"}.`,
    };
  }

  if (key === "checkoutBlock" && experienceType === "personalization/offer") {
    return {
      isDisabled: true,
      reason: `A checkout modification cannot be used in an offer ${isPersonalization ? "personalization" : "test"}.`,
    };
  }

  if (key === "postPurchase" && experienceType === "personalization/offer") {
    return {
      isDisabled: true,
      reason: `A post-purchase modification cannot be used in an offer ${isPersonalization ? "personalization" : "test"}.`,
    };
  }

  if (
    (key === "checkoutBlock" ||
      key === "postPurchase" ||
      key === "onsiteUpsell") &&
    !hasStorefrontToken
  ) {
    return {
      isDisabled: true,
      reason: MISSING_STOREFRONT_TOKEN_ERROR,
    };
  }

  if (modificationsInUse.length === 0) {
    return { isDisabled: false, reason: "" };
  }

  if (modificationsInUse.includes(key)) {
    return {
      isDisabled: true,
      reason: "This modification is already in use.",
      showChecked: true,
    };
  }
  if (key === "onsiteEdits" && isHeadless) {
    return {
      isDisabled: true,
      reason:
        "Content edits are unavailable for headless personalizations. Dynamically replace content on your site directly.",
    };
  }
  if (
    (key === "tieredDiscount" ||
      key === "cartDiscount" ||
      key === "freeGift" ||
      key === "freeShipping" ||
      key === "buyXGetY" ||
      key === "offer") &&
    !isUnderOfferLimit
  ) {
    return {
      isDisabled: true,
      reason:
        "You have reached your offer limit. Archive unused offer personalizations or tests.",
    };
  }

  for (const modification in modificationDisabledReasons) {
    if ([...modificationsInUse, ...tempModsToAdd].includes(modification)) {
      if (key === modification && modificationsInUse.includes(key)) {
        return {
          isDisabled: true,
          reason: "This modification is already in use.",
        };
      }

      if (modificationDisabledReasons[modification].includes(key)) {
        return {
          isDisabled: true,
          reason:
            key === "postPurchase"
              ? `${capitalizeFirstLetter(priceIncompatabilityLabels[modification])} modification cannot be combined with a post-purchase modification.`
              : modification === "postPurchase"
                ? `A post-purchase modification cannot be combined with ${priceIncompatabilityLabels[key]} modification.`
                : key === "template"
                  ? "A theme modification is already in use and cannot be combined with a template modification."
                  : key === "theme"
                    ? "A template modification is already in use and cannot be combined with a theme modification."
                    : key === "price"
                      ? `A price modification cannot be combined with ${priceIncompatabilityLabels[modification]} modification.`
                      : key === "offer" && modification === "price"
                        ? "An offer modification cannot be combined with a price modification."
                        : key === "offer" && modification === "checkoutBlock"
                          ? "An offer modification cannot be combined with a checkout modification."
                          : key === "checkoutBlock"
                            ? `${capitalizeFirstLetter(priceIncompatabilityLabels[modification])} modification cannot be combined with a checkout modification.`
                            : key === "onsiteUpsell"
                              ? `${capitalizeFirstLetter(priceIncompatabilityLabels[modification])} modification cannot be combined with an onsite upsell modification.`
                              : modification === "onsiteUpsell"
                                ? `An onsite upsell modification cannot be combined with ${priceIncompatabilityLabels[key]} modification.`
                                : "Another offer modification is already in use.",
        };
      }
    }
  }

  return { isDisabled: false, reason: "" };
};
